import {Injectable} from '@angular/core';
import {CookieService} from 'ngx-cookie-service';

@Injectable({
  providedIn: 'root'
})
export class FlowCorrelationService {

  private readonly COOKIE_NAME = 'recharges_correlation_id';
  private readonly INSTRUMENT_CODE_COOKIE_NAME = 'recharges_instrument_code';
  private readonly INSTRUMENT_TYPE_COOKIE_NAME = 'recharges_instrument_type';
  private readonly INSTRUMENT_NAME_COOKIE_NAME = 'recharges_instrument_name';
  private readonly COOKIE_DAYS = 2;

  constructor(private cookieService: CookieService) {
  }

  startFlow(businessId?: string): string {
    this.clearInstrumentContext();
    const normalizedBusinessId = this.normalize(businessId);
    if (normalizedBusinessId) {
      this.setCorrelationId(normalizedBusinessId);
      return normalizedBusinessId;
    }
    return this.getOrCreateCorrelationId();
  }

  ensureForIncomingFlow(businessId?: string): string {
    const current = this.getCorrelationId();
    if (current) {
      return current;
    }
    const normalizedBusinessId = this.normalize(businessId);
    if (normalizedBusinessId) {
      this.setCorrelationId(normalizedBusinessId);
      return normalizedBusinessId;
    }
    return this.createAndStoreCorrelationId();
  }

  adoptCorrelationId(correlationId?: string): string {
    const normalizedCorrelationId = this.normalize(correlationId);
    if (!normalizedCorrelationId) {
      return this.ensureForIncomingFlow();
    }
    this.setCorrelationId(normalizedCorrelationId);
    return normalizedCorrelationId;
  }

  appendCorrelationToUrl(url: string): string {
    const correlationId = this.getOrCreateCorrelationId();
    if (!this.normalize(url) || !correlationId || url.indexOf('correlationId=') >= 0) {
      return url;
    }

    const fragmentIndex = url.indexOf('#');
    const fragment = fragmentIndex >= 0 ? url.substring(fragmentIndex) : '';
    const baseUrl = fragmentIndex >= 0 ? url.substring(0, fragmentIndex) : url;
    const separator = baseUrl.indexOf('?') >= 0 ? '&' : '?';
    return baseUrl + separator + 'correlationId=' + encodeURIComponent(correlationId) + fragment;
  }

  getOrCreateCorrelationId(): string {
    const current = this.getCorrelationId();
    if (current) {
      return current;
    }
    return this.createAndStoreCorrelationId();
  }

  getCorrelationId(): string {
    return this.cookieService.get(this.COOKIE_NAME);
  }

  createRequestId(): string {
    return this.createUuid();
  }

  getCorrelationHeaders(): {[key: string]: string} {
    const headers: {[key: string]: string} = {
      'X-Correlation-Id': this.getOrCreateCorrelationId(),
      'X-Request-Id': this.createRequestId()
    };
    this.addInstrumentHeader(headers, 'X-Recharges-Instrument-Code', this.getCookieValue(this.INSTRUMENT_CODE_COOKIE_NAME));
    this.addInstrumentHeader(headers, 'X-Recharges-Instrument-Type', this.getCookieValue(this.INSTRUMENT_TYPE_COOKIE_NAME));
    this.addInstrumentHeader(headers, 'X-Recharges-Instrument-Name', this.getCookieValue(this.INSTRUMENT_NAME_COOKIE_NAME));
    return headers;
  }

  setInstrumentContext(instrumentCode?: string, instrumentType?: string, instrumentName?: string): void {
    const normalizedInstrumentCode = this.normalize(instrumentCode);
    this.setOptionalCookie(this.INSTRUMENT_CODE_COOKIE_NAME, normalizedInstrumentCode);
    this.setOptionalCookie(this.INSTRUMENT_TYPE_COOKIE_NAME, this.normalize(instrumentType) || this.resolveInstrumentType(normalizedInstrumentCode));
    this.setOptionalCookie(this.INSTRUMENT_NAME_COOKIE_NAME, instrumentName || normalizedInstrumentCode);
  }

  clearInstrumentContext(): void {
    this.cookieService.delete(this.INSTRUMENT_CODE_COOKIE_NAME, '/');
    this.cookieService.delete(this.INSTRUMENT_TYPE_COOKIE_NAME, '/');
    this.cookieService.delete(this.INSTRUMENT_NAME_COOKIE_NAME, '/');
  }

  resolveInstrumentType(instrumentCode?: string): string {
    const normalized = this.normalize(instrumentCode).toUpperCase();
    switch (normalized) {
      case 'CARD':
      case 'BLIK':
      case 'GPAY':
      case 'APAY':
        return normalized;
      case '':
        return '';
      default:
        return 'TRANSFER';
    }
  }

  private createAndStoreCorrelationId(): string {
    const correlationId = this.createUuid();
    this.setCorrelationId(correlationId);
    return correlationId;
  }

  private setCorrelationId(correlationId: string): void {
    this.setCookie(this.COOKIE_NAME, correlationId);
  }

  private setOptionalCookie(name: string, value?: string): void {
    const normalizedValue = this.normalize(value);
    if (!normalizedValue) {
      this.cookieService.delete(name, '/');
      return;
    }
    this.setCookie(name, normalizedValue);
  }

  private setCookie(name: string, value: string): void {
    const expires = new Date();
    expires.setDate(expires.getDate() + this.COOKIE_DAYS);
    this.cookieService.set(name, value, {
      path: '/',
      secure: window.location.protocol === 'https:',
      sameSite: 'Lax',
      expires
    });
  }

  private getCookieValue(name: string): string {
    return this.cookieService.get(name);
  }

  private addInstrumentHeader(headers: {[key: string]: string}, name: string, value?: string): void {
    const normalizedValue = this.normalize(value);
    if (normalizedValue) {
      headers[name] = encodeURIComponent(normalizedValue);
    }
  }

  private normalize(value?: string): string {
    return value == null ? '' : value.trim();
  }

  private createUuid(): string {
    const cryptoRef = window.crypto || (window as any).msCrypto;
    if (cryptoRef && typeof cryptoRef.randomUUID === 'function') {
      return cryptoRef.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const random = Math.random() * 16 | 0;
      const value = c === 'x' ? random : (random & 0x3 | 0x8);
      return value.toString(16);
    });
  }
}
